import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";

import "./Match.css"; // Import the updated styles for the form
import useAxios from "./useAxios";
import "./FormStyles.css";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import Select from "react-select";

const Match = () => {
  const { id } = useParams();
  const [querystring] = useSearchParams();
  const isNewMatchWithTable = id === "new" && querystring.get("table") !== null;

  const [match, setMatch] = useState(null);
  const [axios] = useAxios(true);
  const navigate = useNavigate();

  const [players, setPlayers] = useState(null);
  const [tables, setTables] = useState(null);

  const types = [
    { id: "singles", name: "Singles" },
    { id: "doubles", name: "Doubles" },
  ];
  const [type, setType] = useState(types[0]);

  const [statusOptions] = useState([
    { id: 0, name: "Pending" },
    { id: 1, name: "Started" },
    { id: 2, name: "Finished" },
  ]);

  useEffect(() => {
    axios.get("players", setPlayers);
    axios.get("tables", (response) => {
      setTables(response);
      getMatch(response);
    });
  }, []);

  const getMatch = async (tables) => {
    axios.get("match/" + id, (data) => {
      if (isNewMatchWithTable) {
        // Set default values for new match with table
        data.name = "Quick Match";
        data.dateTime = new Date();
        const tableId = parseInt(querystring.get("table"), 10);
        if (tableId) {
          data.table = tables?.find((x) => x.id === tableId);
        }
      }
      setMatch(data);
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setMatch((prevmatch) => ({ ...prevmatch, [name]: value }));
  };

  const handleDateTimeChange = (datetime) => {
    setMatch((prevmatch) => ({ ...prevmatch, dateTime: datetime }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setMatch((prevmatch) => ({ ...prevmatch, [name]: checked }));
  };

  const handlePlayerChange = (side, position, player) => {
    setMatch((prevmatch) => ({
      ...prevmatch,
      [side]: { ...prevmatch[side], [position]: player },
    }));
  };

  const handleTableChange = (table) => {
    setMatch((prevmatch) => ({ ...prevmatch, table: table }));
  };

  const handleStatusChange = (status) => {
    setMatch((prevmatch) => ({ ...prevmatch, status: status.id }));
  };

  const handleScoreChange = (side, field, value) => {
    const parsedValue = typeof value === 'boolean'
      ? value
      : (value === '' ? 0 : parseInt(value));

    setMatch((prevmatch) => ({
      ...prevmatch,
      [side]: { ...prevmatch[side], [field]: parsedValue },
    }));
  };

  const onSave = () => {
    // Only set default status if not explicitly set
    if (match.status === undefined || match.status === null) {
      match.status = match.isStreamed ? 0 : 1;
    }
    
    // Create a copy of match and null out competition to avoid serialization issues
    const matchToSave = { ...match, competition: null };
    
    axios.post(
      "match/" + id,
      matchToSave,
      querystring.get("table") === null
        ? () => navigate("/configuration/matches")
        : () => navigate("/scoreboard/" + querystring.get("table"))
    );
  };

  const onCancel = () => {
    navigate("/configuration/matches");
  };

  if (match === null || players === null || tables === null) {
    return <h1>Loading</h1>;
  }

  return (
    <div className="edit-match-form">
      <h2>{isNewMatchWithTable ? "Quick Match" : "Edit Match"}</h2>
      <form>
        {!isNewMatchWithTable && (
          <div className="form-group">
            <label htmlFor="name">
              <span className="label-text">Name:</span>
            </label>
            <input
              type="text"
              name="name"
              value={match.name ?? ""}
              onChange={handleChange}
              className="form-control"
            />
          </div>
        )}
        {!isNewMatchWithTable && (
          <div className="form-group">
            <label htmlFor="dateTime">
              <span className="label-text">Date/Time:</span>
            </label>
            <DatePicker
              selected={new Date(match.dateTime)}
              onChange={handleDateTimeChange}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              timeCaption="Time"
              dateFormat="dd MMM yyyy HH:mm"
              className="form-control"
            />
          </div>
        )}
        <div className="form-group">
          <label htmlFor="type">
            <span className="label-text">Type:</span>
          </label>
          <Select
            value={type}
            onChange={(option) => setType(option)}
            options={types}
            getOptionValue={(option) => option.id}
            getOptionLabel={(option) => option.name}
            isSearchable={false}
          />
        </div>
        <div className="form-group">
          <label htmlFor="raceTo">
            <span className="label-text">Race To:</span>
          </label>
          <input
            name="raceTo"
            type="number"
            value={match.raceTo}
            onChange={handleChange}
            min={0}
            max={100}
            className="form-control"
          />
        </div>
        <div className="form-group">
          <label htmlFor="status">
            <span className="label-text">Status:</span>
          </label>
          <Select
            value={statusOptions.find(option => option.id === match.status) || statusOptions[0]}
            onChange={handleStatusChange}
            options={statusOptions}
            getOptionValue={(option) => option.id}
            getOptionLabel={(option) => option.name}
            isSearchable={false}
          />
        </div>
        <div className="form-group">
          <label>
            <span className="label-text">Home:</span>
          </label>
          <Select
            key="home-player1"
            value={match.home?.player}
            onChange={(option) => handlePlayerChange("home", "player", option)}
            options={players.filter(
              (x) =>
                x.id !== match.home?.doubles?.id &&
                x.id !== match.away?.player?.id &&
                x.id !== match.away?.doubles?.id
            )}
            isClearable={true}
            placeholder="Select a player..."
            getOptionValue={(option) => option.id}
            getOptionLabel={(option) => `${option.name}${option.handicap ? ` (${option.handicap})` : ''}`}
            isSearchable={false}
          />
          {type.id === "doubles" ? (
            <Select
              key="home-player2"
              value={match.home?.doubles}
              onChange={(option) =>
                handlePlayerChange("home", "doubles", option)
              }
              options={players.filter(
                (x) =>
                  x.id !== match.home?.player?.id &&
                  x.id !== match.away?.player?.id &&
                  x.id !== match.away?.doubles?.id
              )}
              isClearable={true}
              placeholder="Select a player..."
              getOptionValue={(option) => option.id}
              getOptionLabel={(option) => `${option.name}${option.handicap ? ` (${option.handicap})` : ''}`}
              isSearchable={false}
            />
          ) : null}
        </div>
        <div className="form-group">
          <label>
            <span className="label-text">Away:</span>
          </label>
          <Select
            key="away-player1"
            value={match.away?.player}
            onChange={(option) => handlePlayerChange("away", "player", option)}
            options={players.filter(
              (x) =>
                x.id !== match.home?.player?.id &&
                x.id !== match.home?.doubles?.id &&
                x.id !== match.away?.doubles?.id
            )}
            isClearable={true}
            placeholder="Select a player..."
            getOptionValue={(option) => option.id}
            getOptionLabel={(option) => `${option.name}${option.handicap ? ` (${option.handicap})` : ''}`}
            isSearchable={false}
          />
          {type.id === "doubles" ? (
            <Select
              key="away-player2"
              value={match.away?.doubles}
              onChange={(option) =>
                handlePlayerChange("away", "doubles", option)
              }
              options={players.filter(
                (x) =>
                  x.id !== match.home?.player?.id &&
                  x.id !== match.home?.doubles?.id &&
                  x.id !== match.away?.player?.id
              )}
              isClearable={true}
              placeholder="Select a player..."
              getOptionValue={(option) => option.id}
              getOptionLabel={(option) => `${option.name}${option.handicap ? ` (${option.handicap})` : ''}`}
              isSearchable={false}
            />
          ) : null}
        </div>
        <div className="form-group">
          <label>
            <span className="label-text">Home Score:</span>
          </label>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <div>
              <label htmlFor="homeScore" style={{ fontSize: '12px', color: '#666' }}>Score:</label>
              <input
                name="homeScore"
                type="number"
                value={match.home?.score || 0}
                onChange={(e) => handleScoreChange("home", "score", e.target.value)}
                className="form-control"
                style={{ width: '70px' }}
              />
            </div>
            <div>
              <label htmlFor="homeDishes" style={{ fontSize: '12px', color: '#666' }}>Dishes:</label>
              <input
                name="homeDishes"
                type="number"
                value={match.home?.dishes || 0}
                onChange={(e) => handleScoreChange("home", "dishes", e.target.value)}
                min={0}
                className="form-control"
                style={{ width: '70px' }}
              />
            </div>
            <div>
              <label htmlFor="homeWinner" style={{ fontSize: '12px', color: '#666' }}>Winner:</label>
              <input
                type="checkbox"
                name="homeWinner"
                checked={match.home?.winner || false}
                onChange={(e) => handleScoreChange("home", "winner", e.target.checked)}
                className="custom-checkbox"
                style={{ marginLeft: '5px' }}
              />
            </div>
          </div>
        </div>
        <div className="form-group">
          <label>
            <span className="label-text">Away Score:</span>
          </label>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <div>
              <label htmlFor="awayScore" style={{ fontSize: '12px', color: '#666' }}>Score:</label>
              <input
                name="awayScore"
                type="number"
                value={match.away?.score || 0}
                onChange={(e) => handleScoreChange("away", "score", e.target.value)}
                className="form-control"
                style={{ width: '70px' }}
              />
            </div>
            <div>
              <label htmlFor="awayDishes" style={{ fontSize: '12px', color: '#666' }}>Dishes:</label>
              <input
                name="awayDishes"
                type="number"
                value={match.away?.dishes || 0}
                onChange={(e) => handleScoreChange("away", "dishes", e.target.value)}
                min={0}
                className="form-control"
                style={{ width: '70px' }}
              />
            </div>
            <div>
              <label htmlFor="awayWinner" style={{ fontSize: '12px', color: '#666' }}>Winner:</label>
              <input
                type="checkbox"
                name="awayWinner"
                checked={match.away?.winner || false}
                onChange={(e) => handleScoreChange("away", "winner", e.target.checked)}
                className="custom-checkbox"
                style={{ marginLeft: '5px' }}
              />
            </div>
          </div>
        </div>
        {!isNewMatchWithTable && (
          <div className="form-group">
            <label htmlFor="table">
              <span className="label-text">Table:</span>
            </label>
            <Select
              value={match.table}
              onChange={handleTableChange}
              options={tables}
              getOptionValue={(option) => option.id}
              getOptionLabel={(option) => option.name}
              isClearable={true}
              placeholder="Select a table..."
              isSearchable={false}
            />
          </div>
        )}
        {match.table?.streamType && (
          <div className="form-group">
            <label htmlFor="isStreamed">
              <input
                type="checkbox"
                name="isStreamed"
                checked={match.isStreamed}
                onChange={handleCheckboxChange}
                className="custom-checkbox"
              />
              <span className="label-text">Streamed</span>
            </label>
          </div>
        )}
        <div className="form-buttons">
          <button type="button" onClick={onSave} className="submit-button">
            Save
          </button>
          <button type="button" onClick={onCancel} className="cancel-button">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default Match;
